import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import FormCheckbox from '../FormCheckbox';
import { NOT_APPLICABLE } from './NotApplicable.constants';
import { NotApplicableContainer } from './NotApplicable.styled';
import { NotApplicableProps, ToggleChecked } from './NotApplicable.types';

/**
 * NotApplicable component - wrapper that adds additional logic with Not applicable checkbox for field
 * @param {NotApplicableProps} props - NotApplicableProps type
 * @param {T} props.initialValue - initial value - value we set to a field, when checkbox gets unchecked
 * @param {T} props.disabledValue - disabled value - value we set to a field, when checkbox gets checked
 * @param {string} props.fieldName - field name - we can get it directly from <Controller /> field.name
 * @param {ReactNode} props.children - children component with field inputs
 *
 */
function NotApplicable<T>({
  children,
  fieldName,
  initialValue,
  onChange,
  isLoading,
  disabled,
  disabledValue = NOT_APPLICABLE,
}: NotApplicableProps<T>) {
  const { setValue, watch } = useFormContext<Record<string, T | typeof NOT_APPLICABLE>>();

  const toggleChecked = useCallback<ToggleChecked>(
    (_e, _checked) => {
      const currentValue = watch(fieldName);
      const nextValue = currentValue === disabledValue ? initialValue : disabledValue;

      setValue(fieldName, nextValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      onChange(nextValue);
    },
    [watch, fieldName, disabledValue, initialValue, onChange, setValue],
  );

  const fieldValue = watch(fieldName);
  const isChecked = fieldValue === disabledValue;

  return (
    <NotApplicableContainer>
      <>
        {children({ isChecked })}
        {isLoading ? null : (
          <FormCheckbox checked={isChecked} onChange={toggleChecked} label="Not Applicable" disabled={disabled} />
        )}
      </>
    </NotApplicableContainer>
  );
}

export default NotApplicable;
